import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Offcanvas, OffcanvasBody, Input, Label, Button } from "reactstrap";
import { getDeliveryUsers } from "../../api/user";
import { getActiveClientsApi } from "../../api/company";
import { commerceSecondaryLine, matchCommerce, toCommerceOption } from "../../utils/commerceSearch";
import SearchDropdown from "../searchDropdown";
import DateRangeFilter, { rangeFromPreset, DEFAULT_PRESETS } from "../dateRangeFilter/DateRangeFilter";
import { showToast } from "../../utils/showToast";
// Catálogo geográfico desde el módulo `geo` del BFF (no desde
// `contracts/countries/*`): es el único que expone los `_id` de
// `countries.regions[]` y `.communes[]`, que son las claves con las que está
// expresada la cobertura del operador (`logisticscompanies.coverageAreas`).
// Ambos leen la misma colección, así que los nombres son idénticos.
import { getRegions as getGeoRegions, getCommunesByRegion } from "../../api/settings";
import useOperatorCoverage from "../../hooks/useOperatorCoverage";
import { useCountry } from "../../context/CountryContext";
import { roleRoutes } from "../../utils/roles";
import { Filter, X, Calendar, MapPin, User, Package, Tag, RotateCcw, ArrowUpDown } from "lucide-react";
import { packageStatusFilterOptions } from "../../utils/packageStatus";
import { packageTypeFilterOptions } from "../../utils/packageTypeOptions";

const FilterModal = ({ open, activeFilters, callback, closeModal, statusDisabled, user }) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState(activeFilters || {});
  const [clientList, setClientList] = useState([]);
  const [deliveryList, setDeliveryList] = useState([]);
  // Catálogo crudo de `geo`: [{ _id, name, code }]. `regionsList` se deriva de
  // acá acotándolo a la cobertura declarada.
  const [geoRegions, setGeoRegions] = useState([]);
  const [communesList, setCommunesList] = useState([]);
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [isLoadingDelivery, setIsLoadingDelivery] = useState(false);
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);
  const [isLoadingCommunes, setIsLoadingCommunes] = useState(false);

  const logisticCompanyId = user?.logisticCompany?._id || null;
  // Mesa de ayuda (support) comparte las mismas opciones de filtro avanzadas
  // que admin: carga de listas cliente/repartidor, toggle de discrepancia
  // MercadoLibre y la sección "Asignación".
  const isAdmin =
    user?.role === roleRoutes.admin || user?.role === roleRoutes.support;
  const prevOpen = useRef(open);
  // `labels` = cómo se llaman las divisiones administrativas en el país del
  // operador (Región/Comuna en CL, Provincia/Municipio en AR).
  // `postalCodeRates.enabled` gobierna si el país usa código postal como clave
  // operativa; con false (CL hoy) el campo no se muestra siquiera.
  const {
    countryId,
    loading: countryLoading,
    labels,
    postalCodeRates,
  } = useCountry();
  const showPostalCode = postalCodeRates?.enabled === true;

  // ── Cobertura declarada del operador ──────────────────────────────────────
  // Índice regionId -> Set(communeId) | null, donde null = "región completa"
  // (la convención del backend para `communeIds: []`).
  // El índice completo es `null` cuando la cobertura no se conoce (cargando o
  // falló) o cuando el operador no declaró ninguna: en esos casos NO se filtra,
  // porque un modal de filtros sin opciones no deja buscar nada. Es un filtro de
  // solo lectura: a diferencia de la configuración de tarifas, acá no hay riesgo
  // de escribir datos mal.
  const { coverageAreas } = useOperatorCoverage();
  const coverageIndex = useMemo(() => {
    if (!Array.isArray(coverageAreas) || !coverageAreas.length) return null;
    const index = new Map();
    for (const area of coverageAreas) {
      if (!area?.regionId) continue;
      const ids = Array.isArray(area.communeIds) ? area.communeIds.map(String) : [];
      index.set(String(area.regionId), ids.length ? new Set(ids) : null);
    }
    return index.size ? index : null;
  }, [coverageAreas]);

  // Si el cruce dejara la lista vacía (cobertura de otro país, ids que no calzan)
  // se muestra el catálogo completo antes que un desplegable muerto.
  const regionsList = useMemo(() => {
    if (!geoRegions.length) return [];
    const covered = coverageIndex
      ? geoRegions.filter((r) => coverageIndex.has(String(r._id)))
      : geoRegions;
    return (covered.length ? covered : geoRegions).map((r) => ({ value: r.code, label: r.name }));
  }, [geoRegions, coverageIndex]);

  // `filters.regionCode` sigue siendo el `code` de la región (no cambia el
  // contrato del filtro, y de todos modos no viaja al backend); para pedir las
  // comunas a `geo` hace falta su ObjectId.
  const selectedGeoRegion = useMemo(
    () => geoRegions.find((r) => String(r.code) === String(filters.regionCode)) ?? null,
    [geoRegions, filters.regionCode],
  );

  useEffect(() => {
    const fetchData = async () => {
      if (logisticCompanyId && open) {
        setIsLoadingClient(true);
        setIsLoadingDelivery(true);
        try {
          const clients = await getActiveClientsApi(logisticCompanyId);
          const { docs: deliveries } = await getDeliveryUsers("active", "delivery", logisticCompanyId);

          if (Array.isArray(clients)) {
            // El label es el nombre comercial; la razón social queda en la opción para
            // buscarla y mostrarla debajo. Si no hay nombre, la razón social pasa a ser
            // el label (así la segunda línea no la repite) y, sin ella, "Sin nombre". Ese
            // rótulo va DENTRO del nombre para que también se busque: tras elegir, el
            // buscador queda escrito con el label y al reabrir el menú tiene que calzar.
            setClientList(clients.map(client =>
              toCommerceOption(
                { ...client, name: client?.name || client?.legalName || t("Sin nombre") },
                t("Sin nombre"),
              ),
            ));
          }

          if (Array.isArray(deliveries)) {
            setDeliveryList(deliveries.map(delivery => ({
              value: delivery?._id,
              label: `${delivery?.name || ""} ${delivery?.lastName || ""}`.trim(),
            })));
          }
        } catch (error) {
          console.error("Error al obtener datos:", error);
          showToast(t("Error al obtener datos"), false);
        } finally {
          setIsLoadingClient(false);
          setIsLoadingDelivery(false);
        }
      }
    };

    const fetchRegionsData = async () => {
      if (open && countryId) {
        setIsLoadingRegions(true);
        try {
          const res = await getGeoRegions(countryId);
          if (Array.isArray(res?.data)) {
            setGeoRegions(res.data);
          }
        } catch (error) {
          console.error("Error al obtener regiones:", error);
          showToast(t("Error al obtener regiones"), false);
        } finally {
          setIsLoadingRegions(false);
        }
      }
    };

    if (open) {
      // Sin país resuelto todavía no se piden regiones: el efecto se vuelve a
      // disparar cuando `countryId` llega.
      if (!countryLoading) fetchRegionsData();
      if (isAdmin) {
        fetchData();
      }
    }
    prevOpen.current = open;
  }, [logisticCompanyId, open, isAdmin, countryId, countryLoading]);

  useEffect(() => {
    const fetchCommunesData = async () => {
      if (selectedGeoRegion && open) {
        setIsLoadingCommunes(true);
        try {
          const res = await getCommunesByRegion(selectedGeoRegion._id);
          const all = Array.isArray(res?.data?.communes) ? res.data.communes : [];
          // `undefined` (sin cobertura conocida) y `null` (región completa) no
          // filtran; solo un Set acota a las comunas declaradas.
          const allowed = coverageIndex ? coverageIndex.get(String(selectedGeoRegion._id)) : undefined;
          const visible = allowed instanceof Set ? all.filter((c) => allowed.has(String(c._id))) : all;
          setCommunesList((visible.length ? visible : all).map((c) => ({ value: c.name, label: c.name })));
        } catch (error) {
          console.error("Error al obtener comunas:", error);
          showToast(t("Error al obtener comunas"), false);
        } finally {
          setIsLoadingCommunes(false);
        }
      } else {
        setCommunesList([]);
      }
    };

    fetchCommunesData();
  }, [selectedGeoRegion, open, coverageIndex]);

  const formatMMDDYYYY = (yyyyMmDd) => {
    if (!yyyyMmDd) return undefined;
    const [y, m, d] = yyyyMmDd.split("-").map((n) => parseInt(n, 10));
    const mm = String(m).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${mm}/${dd}/${y}`;
  };

  const applyFilters = () => {
    const { dateStart, dateEnd } = filters || {};

    if (dateStart && dateEnd && new Date(dateEnd) < new Date(dateStart)) {
      showToast(t("La fecha Fin no puede ser menor que Inicio."), false);
      return;
    }

    const outgoing = { ...filters };

    outgoing.dateStart = dateStart || "";
    outgoing.dateEnd = dateEnd || "";

    if (dateStart) outgoing.startDateCreation = formatMMDDYYYY(dateStart);
    else delete outgoing.startDateCreation;

    if (dateEnd) outgoing.endDateCreation = formatMMDDYYYY(dateEnd);
    else delete outgoing.endDateCreation;

    delete outgoing.updatedAtSince;

    callback(outgoing);
    closeModal(false);
  };

  const clearFilters = () => {
    setFilters({});
    callback({});
    closeModal(false);
  };

  const orderBy = [
    { value: "createdAt", label: t("Fecha de ingreso") },
    { value: "updatedAt", label: t("Última actualización") },
  ];

  const selectedOrder = orderBy?.find((f) => f.value === filters?.orderBy);
  const selectedRegion = regionsList?.find((f) => f.value === filters?.regionCode);
  const selectedCom = communesList?.find((f) => f.value === filters?.coms);
  const selectedClient = clientList?.find((f) => f.value === filters?.clientId);
  const selectedDelivery = deliveryList?.find((f) => f.value === filters?.deliveryId);

  // Preset activo del rango "Fecha de ingreso" (resalta el chip o "custom").
  // dateStart/dateEnd siguen siendo YMD; la derivación a startDateCreation/
  // endDateCreation en applyFilters queda intacta.
  const datePreset = useMemo(() => {
    const ds = filters?.dateStart;
    const de = filters?.dateEnd;
    if (!ds || !de) return "";
    const match = DEFAULT_PRESETS.find(({ key }) => {
      const [s, e] = rangeFromPreset(key);
      return s.format("YYYY-MM-DD") === ds && e.format("YYYY-MM-DD") === de;
    });
    return match ? match.key : "custom";
  }, [filters?.dateStart, filters?.dateEnd]);

  const hasActiveFilters = useMemo(() => {
    if (!filters || typeof filters !== "object") return false;
    return Object.entries(filters).some(([k, v]) => {
      if (v === undefined || v === null || v === "") return false;
      if (k === "status" && String(v) === "") return false;
      return true;
    });
  }, [filters]);

  const fieldClass = (active) =>
    `modern-filter-modal__field${active ? " modern-filter-modal__field--active" : ""}`;

  const fieldItemClass = (active) =>
    `modern-filter-modal__field-item${active ? " modern-filter-modal__field-item--active" : ""}`;

  return (
    <Offcanvas
      isOpen={open}
      toggle={() => closeModal(false)}
      direction="end"
      className="modern-filter-modal filter-drawer"
      aria-labelledby="filter-modal-title"
    >
      <OffcanvasBody className="modern-filter-modal__body">
        {/* Header limpio y minimalista */}
        <div className="modern-filter-modal__header">
          <div className="modern-filter-modal__header-title">
            <Filter size={20} className="modern-filter-modal__title-icon" />
            <h2 id="filter-modal-title" className="modern-filter-modal__title">{t("Filtrar y ordenar")}</h2>
          </div>
          <button 
            className="modern-filter-modal__close" 
            onClick={() => closeModal(false)}
            aria-label={t("Cerrar")}
            type="button"
          >
            <X size={18} />
          </button>
        </div>

        {/* Contenido con mejor organización */}
        <div className="modern-filter-modal__content">
          {/* Ordenamiento + Estado (misma fila, dos columnas) */}
          <div className="modern-filter-modal__field">
            <div className="modern-filter-modal__row">
              <div
                className={fieldItemClass(!!filters?.orderBy)}
                role="group"
                aria-labelledby="filter-section-order-label"
              >
                <div className="modern-filter-modal__field-header" id="filter-section-order-label">
                  <ArrowUpDown size={16} className="modern-filter-modal__field-icon" aria-hidden />
                  <Label className="modern-filter-modal__label">{t("Ordenar por")}</Label>
                </div>
                <SearchDropdown
                  onChange={(value) => setFilters({ ...filters, orderBy: value })}
                  options={orderBy}
                  placeholder={t("Seleccione...")}
                  selected={selectedOrder?.label}
                  ariaLabel={t("Ordenar resultados por campo")}
                />
              </div>

              <div
                className={fieldItemClass(
                  filters?.status !== undefined && filters?.status !== ""
                )}
                role="group"
                aria-labelledby="filter-section-status-label"
              >
                <div className="modern-filter-modal__field-header" id="filter-section-status-label">
                  <Package size={16} className="modern-filter-modal__field-icon" aria-hidden />
                  <Label className="modern-filter-modal__label">{t("Estado del paquete")}</Label>
                </div>
                <SearchDropdown
                  onChange={(value) => setFilters({ ...filters, status: value })}
                  options={[
                    { value: "", label: t("Todos los estados") },
                    ...packageStatusFilterOptions,
                  ]}
                  placeholder={t("Seleccione un estado...")}
                  selected={
                    filters?.status !== undefined && filters?.status !== ""
                      ? [
                          { value: "", label: t("Todos los estados") },
                          ...packageStatusFilterOptions,
                        ].find((s) => String(s.value) === String(filters?.status))?.label
                      : ""
                  }
                  disabled={statusDisabled || !!filters?.meliStatusMismatch}
                  ariaLabel={t("Filtrar por estado del paquete")}
                />
              </div>
            </div>
          </div>

          {/* Tipo de paquete (origen / integración del pedido) */}
          <div
            className={fieldClass(filters?.type !== undefined && filters?.type !== "")}
            role="group"
            aria-labelledby="filter-section-type-label"
          >
            <div className="modern-filter-modal__field-header" id="filter-section-type-label">
              <Tag size={16} className="modern-filter-modal__field-icon" aria-hidden />
              <Label className="modern-filter-modal__label">{t("Tipo de paquete")}</Label>
            </div>
            <SearchDropdown
              onChange={(value) => setFilters({ ...filters, type: value })}
              options={[
                { value: "", label: t("Todos los tipos") },
                ...packageTypeFilterOptions,
              ]}
              placeholder={t("Seleccione un tipo...")}
              selected={
                filters?.type !== undefined && filters?.type !== ""
                  ? [
                      { value: "", label: t("Todos los tipos") },
                      ...packageTypeFilterOptions,
                    ].find((tp) => String(tp.value) === String(filters?.type))?.label
                  : ""
              }
              disabled={!!filters?.meliStatusMismatch}
              ariaLabel={t("Filtrar por tipo de paquete")}
            />
          </div>

          {/* Discrepancia MercadoLibre (admin y mesa de ayuda) */}
          {isAdmin && (
            <div
              className={fieldClass(!!filters?.meliStatusMismatch)}
              role="group"
              aria-label={t("Filtro de discrepancia MercadoLibre")}
            >
              <label className="modern-filter-modal__toggle" htmlFor="meliStatusMismatch">
                <div className="modern-filter-modal__toggle-track">
                  <input
                    id="meliStatusMismatch"
                    type="checkbox"
                    className="modern-filter-modal__toggle-input"
                    checked={!!filters?.meliStatusMismatch}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        meliStatusMismatch: e.target.checked ? true : undefined,
                        status: e.target.checked ? undefined : filters?.status,
                        type: e.target.checked ? undefined : filters?.type,
                      })
                    }
                  />
                  <span className="modern-filter-modal__toggle-slider" aria-hidden="true" />
                </div>
                <div className="modern-filter-modal__toggle-content">
                  <span className="modern-filter-modal__toggle-label">{t("Discrepancia MercadoLibre")}</span>
                  <span className="modern-filter-modal__toggle-hint">
                    {t("Entregados en Beartrack pero no confirmados en ML")}
                  </span>
                </div>
              </label>
            </div>
          )}

          {/* Ubicación */}
          <div
            className={fieldClass(!!filters?.regionCode || !!filters?.coms || !!filters?.zipCode)}
            role="group"
            aria-labelledby="filter-section-location-label"
          >
            <div className="modern-filter-modal__field-header" id="filter-section-location-label">
              <MapPin size={16} className="modern-filter-modal__field-icon" aria-hidden />
              <Label className="modern-filter-modal__label">{t("Ubicación de entrega")}</Label>
            </div>
            <div className="modern-filter-modal__row">
              <div className="modern-filter-modal__field-item">
                <Label className="modern-filter-modal__sub-label">{t(labels.region)}</Label>
                <SearchDropdown
                  onChange={(value) => setFilters({ ...filters, regionCode: value, coms: undefined })}
                  options={regionsList}
                  placeholder={isLoadingRegions ? t("Cargando...") : t("Seleccione...")}
                  selected={selectedRegion?.label}
                  disabled={isLoadingRegions}
                  ariaLabel={t("Seleccionar {{region}} de entrega", { region: labels.region.toLowerCase() })}
                  ariaBusy={isLoadingRegions}
                />
              </div>
              <div className="modern-filter-modal__field-item">
                <Label className="modern-filter-modal__sub-label">{t(labels.commune)}</Label>
                <SearchDropdown
                  onChange={(value) => setFilters({ ...filters, coms: value })}
                  options={communesList}
                  placeholder={
                    isLoadingCommunes
                      ? t("Cargando...")
                      : filters.regionCode
                        ? t("Seleccione...")
                        : `${t("Primero seleccione")} ${labels.region.toLowerCase()}`
                  }
                  selected={selectedCom?.label}
                  disabled={isLoadingCommunes || !filters.regionCode}
                  ariaLabel={t("Seleccionar {{commune}} de entrega", { commune: labels.commune.toLowerCase() })}
                  ariaBusy={isLoadingCommunes}
                />
              </div>
              {/* Código postal: solo en países que lo usan como clave operativa
                  (postalCodeRates.enabled). Un operador chileno no ve el campo. */}
              {showPostalCode && (
                <div className="modern-filter-modal__field-item">
                  <Label className="modern-filter-modal__sub-label" for="filter-zip-code">
                    {t("Código postal")}
                  </Label>
                  <Input
                    id="filter-zip-code"
                    type="text"
                    value={filters?.zipCode || ""}
                    // Validación suave a propósito: el CP se escribe a mano y el
                    // backend tolera CPA, CP4 y valores sueltos. Solo se fuerza
                    // mayúscula (así se guarda) y se acota el largo al del campo.
                    maxLength={12}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        zipCode: e.target.value.toUpperCase().slice(0, 12) || undefined,
                      })
                    }
                    placeholder={t("Ej: 5501 o M5501ABC")}
                    aria-label={t("Filtrar por código postal de entrega")}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Asignación (admin y mesa de ayuda) */}
          {isAdmin && (
            <div
              className={fieldClass(!!filters?.clientId || !!filters?.deliveryId)}
              role="group"
              aria-labelledby="filter-section-assignment-label"
            >
                <div className="modern-filter-modal__field-header" id="filter-section-assignment-label">
                  <User size={16} className="modern-filter-modal__field-icon" aria-hidden />
                  <Label className="modern-filter-modal__label">{t("Asignación")}</Label>
                </div>
                <div className="modern-filter-modal__row">
                  <div className="modern-filter-modal__field-item">
                    <Label className="modern-filter-modal__sub-label">{t("Cliente")}</Label>
                    <SearchDropdown
                      onChange={(value) => setFilters({ ...filters, clientId: value })}
                      options={clientList}
                      placeholder={isLoadingClient ? t("Cargando...") : t("Seleccione...")}
                      selected={selectedClient?.label}
                      disabled={isLoadingClient}
                      tooltip
                      filterOption={matchCommerce}
                      getOptionSecondary={commerceSecondaryLine}
                      ariaLabel={t("Filtrar por cliente")}
                      ariaBusy={isLoadingClient}
                    />
                  </div>
                  <div className="modern-filter-modal__field-item">
                    <Label className="modern-filter-modal__sub-label">{t("Repartidor")}</Label>
                    <SearchDropdown
                      onChange={(value) => setFilters({ ...filters, deliveryId: value })}
                      options={deliveryList}
                      placeholder={isLoadingDelivery ? t("Cargando...") : t("Seleccione...")}
                      selected={selectedDelivery?.label}
                      disabled={isLoadingDelivery}
                      tooltip
                      ariaLabel={t("Filtrar por repartidor")}
                      ariaBusy={isLoadingDelivery}
                    />
                  </div>
                </div>
              </div>
          )}

          {/* Fechas */}
          <div
            className={fieldClass(!!filters?.dateStart || !!filters?.dateEnd)}
            role="group"
            aria-labelledby="filter-section-dates-label"
          >
            <div className="modern-filter-modal__field-header" id="filter-section-dates-label">
              <Calendar size={16} className="modern-filter-modal__field-icon" aria-hidden />
              <Label className="modern-filter-modal__label">{t("Fecha de ingreso")}</Label>
            </div>
            <DateRangeFilter
              compact
              value={datePreset}
              presets={DEFAULT_PRESETS}
              customRange={filters?.dateStart && filters?.dateEnd ? { start: filters.dateStart, end: filters.dateEnd } : null}
              onChange={(s, e) => setFilters({ ...filters, dateStart: s, dateEnd: e })}
              onClear={() => setFilters({ ...filters, dateStart: "", dateEnd: "" })}
            />
          </div>
        </div>

        {/* Footer limpio */}
        <div className="modern-filter-modal__footer">
          <Button
            outline
            className="modern-filter-modal__btn modern-filter-modal__btn--secondary"
            onClick={clearFilters}
            type="button"
            disabled={!hasActiveFilters}
          >
            <RotateCcw size={16} aria-hidden />
            {t("Limpiar filtros")}
          </Button>
          <Button
            color="primary"
            className="modern-filter-modal__btn modern-filter-modal__btn--primary"
            onClick={applyFilters}
            type="button"
            aria-label={t("Aplicar filtros seleccionados y cerrar")}
          >
            {t("Aplicar filtros")}
          </Button>
        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default FilterModal;
